import React, { useEffect, useState, useRef } from 'react';
import { Button, Card, Tooltip } from 'antd';
import { BulbOutlined, BulbFilled, ClockCircleFilled } from '@ant-design/icons';
import { BrowserQRCodeReader } from '@zxing/library';
import { ReactComponent as IconTorchOffSvg } from '@assets/images/customer/torch_off.svg';
import { ReactComponent as IconTorchOnSvg } from '@assets/images/customer/torch_on.svg';

export const ScanQR = ({ onChange, isCameraVisible, setIsCameraVisible }) => {
  const [scanResult, setScanResult] = useState('');
  const [isFlashlightOn, setIsFlashlightOn] = useState(false);
  const videoTrackRef = useRef(null);
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const codeReader = useRef(new BrowserQRCodeReader());

  const handleScan = (data) => {
    if (data && data !== scanResult) {
      // Check if scan result is different
      setScanResult(data);
      onChange && onChange(data?.text);
      setIsCameraVisible(false);
      toggleFlashlight(false);
      codeReader.current.reset(); // Stop the decoding process after the scan
    }
  };

  const handleError = (err) => {
    console.error(err);
  };

  const toggleCamera = async (status) => {
    setIsCameraVisible(status);
    if (status) {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: 'environment' }
        });
        streamRef.current = stream;
        videoRef.current.srcObject = stream;

        // Wait for the video to load metadata before playing
        videoRef.current.onloadedmetadata = () => {
          videoRef.current.play().catch((error) => {
            console.error('Video play error:', error);
          });
        };

        // Start the QR reader
        codeReader.current.reset(); // Ensure previous session is cleared
        codeReader.current.decodeFromVideoDevice(null, videoRef.current, (result, error) => {
          if (result) handleScan(result);
          if (error && error !== 'NotFoundException') handleError(error); // Handle specific errors
        });
      } catch (error) {
        console.error('Camera access error:', error);
      }
    } else {
      // Stop the video stream and release the camera
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((track) => track.stop());
        streamRef.current = null;
      }
      codeReader.current.reset(); // Reset the reader when camera is closed
    }
  };

  const toggleFlashlight = async (status) => {
    if (streamRef.current) {
      // Get the video track from the stream
      const videoTrack = streamRef.current.getVideoTracks()[0];
      if (videoTrack) {
        const capabilities = videoTrack.getCapabilities();
        if (capabilities.torch) {
          try {
            await videoTrack.applyConstraints({
              advanced: [{ torch: status }]
            });
            setIsFlashlightOn(status);
          } catch (error) {
            console.error('Error toggling flashlight:', error);
          }
        }
      }
    }
  };

  useEffect(() => {
    if (isCameraVisible) {
      toggleCamera(true);
    }

    return () => {
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((track) => track.stop());
        streamRef.current = null;
      }
      codeReader.current.reset();
    };
  }, [isCameraVisible]);

  const scannerStyle = {
    width: '100%',
    height: '220px',
    borderRadius: 0,
    overflow: 'hidden',
    position: 'relative',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    marginTop: '16px'
  };

  const frameStyle = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: '60%',
    height: '70%',
    pointerEvents: 'none',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center'
  };

  const cornerStyle = {
    position: 'absolute',
    width: '40px',
    height: '40px'
  };

  const cornerPositions = {
    topLeft: {
      top: '-2px',
      left: '-2px',
      borderTop: '2px solid #FFF',
      borderLeft: '2px solid #FFF'
    },
    topRight: {
      top: '-2px',
      right: '-2px',
      borderTop: '2px solid #FFF',
      borderRight: '2px solid #FFF'
    },
    bottomLeft: {
      bottom: '-2px',
      left: '-2px',
      borderBottom: '2px solid #FFF',
      borderLeft: '2px solid #FFF'
    },
    bottomRight: {
      bottom: '-2px',
      right: '-2px',
      borderBottom: '2px solid #FFF',
      borderRight: '2px solid #FFF'
    }
  };

  return (
    <Card className="custom-card" bodyStyle={{ padding: '0px' }}>
      {isCameraVisible && (
        <div style={scannerStyle}>
          <video
            ref={videoRef}
            style={{
              width: '100%',
              height: 'auto',
              objectFit: 'cover',
              borderRadius: 0
            }}
            autoPlay
            muted
          />
          <div style={frameStyle}>
            <div style={{ ...cornerStyle, ...cornerPositions.topLeft }}></div>
            <div style={{ ...cornerStyle, ...cornerPositions.topRight }}></div>
            <div style={{ ...cornerStyle, ...cornerPositions.bottomLeft }}></div>
            <div style={{ ...cornerStyle, ...cornerPositions.bottomRight }}></div>
          </div>

          <Tooltip title={isFlashlightOn ? 'Tắt đèn flash' : 'Bật đèn flash'}>
            <Button
              type="primary"
              shape="circle"
              icon={isFlashlightOn ? <IconTorchOnSvg /> : <IconTorchOffSvg />}
              onClick={() => toggleFlashlight(!isFlashlightOn)}
              style={{
                position: 'absolute',
                bottom: '10px',
                right: '10px',
                zIndex: 1,
                width: '40px',
                height: '40px',
                backgroundColor: isFlashlightOn ? '#FFFFFF' : '#FFFFFF33'
              }}
            />
          </Tooltip>
        </div>
      )}
      {isCameraVisible && (
        <Button
          type="primary"
          style={{
            backgroundColor: '#FFF',
            borderWidth: '1px',
            borderColor: '#E4E4E7',
            width: '100%',
            marginTop: '24px',
            color: '#18181B',
            fontWeight: '600',
            height: '44px'
          }}
          onClick={() => toggleCamera(false)}>
          Huỷ bỏ
        </Button>
      )}
    </Card>
  );
};
